import { API_ENDPOINTS } from "../../../../constants/constants";
import { toast } from "react-toastify";
import { useCallback, useEffect, useState } from "react";
import { COURSE_TO_YOUTUBE_ID_MAP } from "./utils";

// ==========================================
// Types et interfaces
// ==========================================

interface Course {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  order: number;
  moduleId: string;
  time: number;
  locked: boolean;
  completed: boolean;
  createdAt: string;
  updatedAt: string;
  isGreyed?: boolean;
  progression?: number;
}

interface Module {
  id: string;
  title: string;
  description: string;
  order: number;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  courses: Course[];
}

interface CourseProgress {
  id: string;
  userId: string;
  courseId: string;
  progression: number;
  completed: boolean;
  lastWatchedAt: string;
}

interface ModuleProgress {
  id: string;
  userId: string;
  moduleId: string;
  completed: boolean;
  completedAt: string;
}

interface ApiProgressItem {
  id?: string;
  userId?: string;
  courseId?: string;
  id_formation?: string;
  progression?: number | string;
  completed?: boolean;
  lastWatchedAt?: string;
  updatedAt?: string;
  createdAt?: string;
  isAccessible?: boolean;
  isGreyed?: boolean;
}

interface ApiCourse {
  id: string | number;
  moduleId: string;
  title?: string;
  description?: string;
}

// ==========================================
// Données fictives pour les tests (fallback)
// ==========================================

const MOCK_MODULES: Module[] = [
  {
    id: "1",
    title: "Module 1: Introduction",
    description: "Module d'introduction à la formation",
    order: 1,
    isEnabled: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    courses: [
      {
        id: "1",
        title: "Bienvenue à la formation",
        description: "Présentation générale",
        videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
        order: 1,
        moduleId: "1",
        time: 600, // 10 minutes en secondes
        locked: false,
        completed: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: "2",
        title: "Premiers pas",
        description: "Début de votre parcours",
        videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
        order: 2,
        moduleId: "1",
        time: 900, // 15 minutes en secondes
        locked: false,
        completed: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
  },
  {
    id: "2",
    title: "Module 2: Avancé",
    description: "Concepts avancés",
    order: 2,
    isEnabled: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    courses: [],
  },
];

const MOCK_MODULE_PROGRESS: ModuleProgress[] = [
  {
    id: "1",
    userId: "user1",
    moduleId: "1",
    completed: true,
    completedAt: new Date().toISOString(),
  },
];

const MOCK_COURSE_PROGRESS: CourseProgress[] = [
  {
    id: "1",
    userId: "user1",
    courseId: "1",
    progression: 100,
    completed: true,
    lastWatchedAt: new Date().toISOString(),
  },
];

// ==========================================
// Fonctions utilitaires
// ==========================================

/**
 * Assure qu'une valeur est un nombre
 * @param {any} value Valeur à convertir
 * @param {number} defaultValue Valeur par défaut si conversion impossible
 * @returns {number} Valeur numérique
 */
const ensureNumber = (value: number | string, defaultValue = 0): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
};

/**
 * Assure qu'une valeur est une chaîne
 * @param {any} value Valeur à convertir
 * @param {string} defaultValue Valeur par défaut si null/undefined
 * @returns {string} Valeur en chaîne
 */
const ensureString = (
  value: string | number | null | undefined,
  defaultValue = ""
): string => {
  if (value === null || value === undefined) return defaultValue;
  return String(value);
};

/**
 * Trie un tableau d'objets par propriété 'order'
 * @param {Array} items Tableau d'objets à trier
 * @returns {Array} Tableau trié
 */
const sortByOrder = <T extends { order?: number }>(items: T[]): T[] => {
  return [...items].sort((a, b) => {
    const orderA = typeof a.order === "number" ? a.order : 999;
    const orderB = typeof b.order === "number" ? b.order : 999;
    return orderA - orderB;
  });
};

/**
 * Normalise un objet de progression de cours
 * @param {Object} item Objet de progression brut
 * @returns {CourseProgress} Objet normalisé
 */
const normalizeProgressItem = (item: ApiProgressItem): CourseProgress => ({
  id: ensureString(item.id, Math.random().toString(36).substr(2, 9)),
  userId: ensureString(item.userId, localStorage.getItem("userId") || ""),
  courseId: ensureString(item.courseId || item.id),
  progression: ensureNumber(item.progression || 0),
  completed: Boolean(
    item.completed || item.isAccessible === true || item.isGreyed === false
  ),
  lastWatchedAt: ensureString(
    item.updatedAt || item.createdAt,
    new Date().toISOString()
  ),
});

/**
 * Vérifie si un token d'accès est disponible
 * @returns {string|null} Token d'accès ou null
 */
const getAccessToken = (): string | null => {
  const accessToken = localStorage.getItem("accessToken");
  return accessToken;
};

// ==========================================
// Fonction centrale de requête API
// ==========================================

interface RequestOptions {
  method: string;
  headers: Record<string, string>;
  signal: AbortSignal;
  body?: string;
}

type RequestBody = Record<string, unknown> | null;

/**
 * Effectue une requête API avec gestion du cache et des erreurs
 * @param {string} url URL de l'endpoint
 * @param {string} method Méthode HTTP (GET, POST, PUT, etc.)
 * @param {Object|null} body Corps de la requête (pour POST, PUT)
 * @param {number} timeoutMs Timeout en millisecondes
 * @param {number} retryCount Nombre de tentatives en cas d'échec
 * @returns {Promise<unknown>} Réponse de l'API
 */
const makeApiRequest = async (
  url: string,
  method: string,
  body: RequestBody = null,
  timeoutMs = 8000,
  retryCount = 2
): Promise<unknown> => {
  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error("Vous n'êtes pas connecté");
  }

  // Ajouter un paramètre anti-cache pour les requêtes GET
  const finalUrl =
    method === "GET"
      ? `${url}${url.includes("?") ? "&" : "?"}_t=${Date.now()}`
      : url;

  // Fonction pour exécuter une tentative
  const executeAttempt = async (attempt: number): Promise<unknown> => {
    // Configuration de l'AbortController pour le timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      console.log(
        `Tentative ${attempt + 1}/${retryCount + 1} pour ${method} ${url}`
      );

      const options: RequestOptions = {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Accept: "*/*",
          Origin: window.location.origin,
        },
        signal: controller.signal,
      };

      if (body) {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(finalUrl, options);

      // Vérifier si la réponse est OK
      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorMessage = `Erreur lors de la requête ${method} sur ${url}`;

        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        }

        throw new Error(errorMessage);
      }

      // Pour les réponses vides (204 No Content)
      if (response.status === 204) {
        return null;
      }

      // Tenter de parser la réponse JSON
      const data = await response.json();
      return data;
    } catch (error) {
      // Gérer l'erreur de timeout spécifiquement
      if (error instanceof DOMException && error.name === "AbortError") {
        if (attempt < retryCount) {
          console.warn(
            `Timeout lors de la tentative ${attempt + 1}, nouvelle tentative...`
          );
          // Attendre un peu avant de réessayer (backoff exponentiel)
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * Math.pow(2, attempt))
          );
          return executeAttempt(attempt + 1);
        }

        toast.error(
          "La requête a pris trop de temps à s'exécuter malgré plusieurs tentatives."
        );
        throw new Error(
          "Timeout: La requête a pris trop de temps à s'exécuter."
        );
      }

      // Pour les autres erreurs, réessayer si possible
      if (attempt < retryCount) {
        console.warn(
          `Erreur lors de la tentative ${attempt + 1}, nouvelle tentative...`,
          error
        );
        // Attendre un peu avant de réessayer
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * Math.pow(2, attempt))
        );
        return executeAttempt(attempt + 1);
      }

      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Démarrer la première tentative
  return executeAttempt(0);
};

// ==========================================
// Wrapper pour la gestion des erreurs et du mode développement
// ==========================================

type ApiFunction<T extends unknown[], R> = (...args: T) => Promise<R>;

/**
 * Wrapper pour gérer les erreurs et le mode développement
 * @param {Function} apiCall Fonction d'appel API
 * @param {any} mockData Données fictives à retourner en cas d'erreur en dev
 * @returns {Function} Fonction wrapped
 */
const withErrorHandling = <T extends unknown[], R>(
  apiCall: ApiFunction<T, R>,
  mockData: R | ((...args: T) => R)
): ApiFunction<T, R> => {
  return async (...args: T): Promise<R> => {
    try {
      return await apiCall(...args);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error(`Erreur API: ${errorMessage}`);

      // En mode développement, retourner des données mock
      if (import.meta.env.DEV) {
        console.warn("Utilisation de données de test suite à une erreur");
        return typeof mockData === "function"
          ? (mockData as (...args: T) => R)(...args)
          : mockData;
      }

      // En production, relancer l'erreur
      throw error;
    }
  };
};

// ==========================================
// Adaptateurs pour traiter les réponses API
// ==========================================

interface ApiResponseWithActiveModule {
  activeModule?: Module;
  otherModules?: Module[];
  accessibleModules?: Module[] | string[];
}

interface ApiProgressResponse {
  accessibleModules?: string[];
  completedModules?: string[];
  allCourses?: ApiCourse[];
}

/**
 * Adapte la réponse de modules de l'API
 * @param {any} data Données de la réponse API
 * @returns {Module[]} Modules normalisés
 */
const adaptModulesResponse = (data: unknown): Module[] => {
  // Cas où l'API renvoie un objet avec les modules actifs et autres
  if (data && typeof data === "object" && "activeModule" in data) {
    const { activeModule, otherModules, accessibleModules } =
      data as ApiResponseWithActiveModule;
    const processedModules: Module[] = [];

    // Ajouter le module actif s'il existe
    if (activeModule) {
      processedModules.push({
        ...activeModule,
        isEnabled: true, // Le module actif est toujours activé
      });
    }

    // Ajouter les autres modules avec leur statut d'activation
    if (Array.isArray(otherModules)) {
      otherModules.forEach((module) => {
        const isEnabled =
          Array.isArray(accessibleModules) &&
          accessibleModules.some((m) =>
            typeof m === "string" ? m === module.id : m.id === module.id
          );

        processedModules.push({
          ...module,
          isEnabled: isEnabled,
        });
      });
    }

    return sortByOrder(processedModules);
  }
  // Cas où l'API renvoie directement un tableau de modules
  else if (Array.isArray(data) && data.length > 0) {
    const sortedModules = sortByOrder(data as Module[]);

    // Marquer tous les modules comme activés par défaut si l'API ne le spécifie pas
    return sortedModules.map((module, index) => ({
      ...module,
      isEnabled: index === 0, // Par défaut, seul le premier module est activé
    }));
  }

  // Aucun module trouvé
  return [];
};

/**
 * Adapte la réponse de progression de l'API
 * @param {any} data Données de la réponse API
 * @param {Module[]} allModules Tous les modules disponibles (facultatif)
 * @returns {CourseProgress[]} Progressions normalisées
 */
const adaptProgressResponse = (
  data: unknown,
  allModules: Module[] = []
): CourseProgress[] => {
  // Si c'est déjà un tableau de progressions
  if (Array.isArray(data)) {
    return data.map((item) => normalizeProgressItem(item as ApiProgressItem));
  }

  // Si c'est l'objet avec accessibleModules, completedModules, etc.
  if (data && typeof data === "object" && "accessibleModules" in data) {
    const apiData = data as ApiProgressResponse;

    // Utiliser allCourses si disponible
    if (apiData.allCourses && Array.isArray(apiData.allCourses)) {
      return apiData.allCourses.map((course: ApiCourse) => {
        const isAccessible =
          Array.isArray(apiData.accessibleModules) &&
          apiData.accessibleModules.includes(course.moduleId);
        const isCompleted =
          apiData.completedModules &&
          Array.isArray(apiData.completedModules) &&
          apiData.completedModules.includes(course.moduleId);

        return {
          id: Math.random().toString(36).substr(2, 9),
          userId: localStorage.getItem("userId") || "",
          courseId: String(course.id) || "",
          progression: isCompleted ? 100 : isAccessible ? 0 : -1,
          completed: Boolean(isCompleted),
          lastWatchedAt: new Date().toISOString(),
        };
      });
    } else if (allModules.length > 0) {
      // Si nous avons accès à tous les modules, créer les progressions
      let coursesProgress: CourseProgress[] = [];

      // Créer des progressions pour chaque cours des modules accessibles
      if (Array.isArray(apiData.accessibleModules)) {
        for (const moduleId of apiData.accessibleModules) {
          const module = allModules.find((m) => m.id === moduleId);

          if (module && module.courses && module.courses.length > 0) {
            const moduleCoursesProgress = module.courses.map((course) => ({
              id: Math.random().toString(36).substr(2, 9),
              userId: localStorage.getItem("userId") || "",
              courseId: course.id,
              progression: 0,
              completed: false,
              lastWatchedAt: new Date().toISOString(),
            }));

            coursesProgress = [...coursesProgress, ...moduleCoursesProgress];
          } else {
            coursesProgress.push({
              id: Math.random().toString(36).substr(2, 9),
              userId: localStorage.getItem("userId") || "",
              courseId: moduleId,
              progression: 0,
              completed: false,
              lastWatchedAt: new Date().toISOString(),
            });
          }
        }
      }

      // Créer des progressions pour chaque cours des modules complétés
      if (apiData.completedModules && Array.isArray(apiData.completedModules)) {
        for (const moduleId of apiData.completedModules) {
          const module = allModules.find((m) => m.id === moduleId);

          if (module && module.courses && module.courses.length > 0) {
            const moduleCoursesProgress = module.courses.map((course) => ({
              id: Math.random().toString(36).substr(2, 9),
              userId: localStorage.getItem("userId") || "",
              courseId: course.id,
              progression: 100,
              completed: true,
              lastWatchedAt: new Date().toISOString(),
            }));

            coursesProgress = [...coursesProgress, ...moduleCoursesProgress];
          } else {
            coursesProgress.push({
              id: Math.random().toString(36).substr(2, 9),
              userId: localStorage.getItem("userId") || "",
              courseId: moduleId,
              progression: 100,
              completed: true,
              lastWatchedAt: new Date().toISOString(),
            });
          }
        }
      }

      return coursesProgress;
    }
  }

  return [];
};

// ==========================================
// Fonctions d'API implémentées
// ==========================================

/**
 * Récupère tous les modules de formation
 * @returns {Promise<Module[]>} Tableau de modules
 */
export const fetchModules = withErrorHandling(async () => {
  const data = await makeApiRequest(API_ENDPOINTS.CINEPRO.GET_MODULES, "GET");
  return adaptModulesResponse(data);
}, MOCK_MODULES);

/**
 * Récupère les cours d'un module spécifique
 * @param {string} moduleId ID du module
 * @returns {Promise<Course[]>} Tableau de cours
 */
export const fetchModuleCourses = withErrorHandling(
  async (moduleId: string) => {
    const data = await makeApiRequest(
      API_ENDPOINTS.CINEPRO.GET_MODULE_COURSES(moduleId),
      "GET"
    );

    // Trier les cours par leur ordre
    return sortByOrder(data as Course[]);
  },
  (moduleId: string) => {
    const mockModule = MOCK_MODULES.find((m) => m.id === moduleId);
    return mockModule?.courses || [];
  }
);

/**
 * Récupère l'accessibilité et l'état de complétion des modules
 * @returns {Promise<{accessibleModules: string[], completedModules: string[]}>}
 */
export const fetchModuleAccessibility = withErrorHandling(async () => {
  const data = await makeApiRequest(
    API_ENDPOINTS.CINEPRO.GET_PROGRESSION,
    "GET"
  );
  
  if (data && typeof data === "object" && "accessibleModules" in data) {
    return {
      accessibleModules: (data as { accessibleModules?: string[] }).accessibleModules || [],
      completedModules: (data as { completedModules?: string[] }).completedModules || [],
    };
  }
  
  return { accessibleModules: [], completedModules: [] };
}, { accessibleModules: [], completedModules: [] });

/**
 * Récupère les progressions des modules
 * @returns {Promise<ModuleProgress[]>} Tableau de progressions de modules
 */
export const fetchModuleProgressions = withErrorHandling(async () => {
  const data = await makeApiRequest(
    API_ENDPOINTS.CINEPRO.GET_PROGRESSION,
    "GET"
  );
  return Array.isArray(data) ? (data as ModuleProgress[]) : [];
}, MOCK_MODULE_PROGRESS);

/**
 * Récupère les progressions de tous les cours de l'utilisateur
 * @returns {Promise<CourseProgress[]>} Tableau de progressions de cours
 */
export const fetchUserProgress = withErrorHandling(async () => {
  // Récupérer d'abord tous les modules pour avoir la structure complète si nécessaire
  let allModules: Module[] = [];
  try {
    allModules = await fetchModules();
  } catch (err) {
    console.warn(
      "Impossible de récupérer les modules pour la progression:",
      err
    );
  }

  const data = await makeApiRequest(
    API_ENDPOINTS.CINEPRO.GET_PROGRESSION,
    "GET"
  );

  return adaptProgressResponse(data, allModules);
}, MOCK_COURSE_PROGRESS);

/**
 * Récupère la progression d'un cours spécifique
 * @param {string} courseId ID du cours
 * @returns {Promise<CourseProgress|null>} Progression du cours ou null
 */
export const fetchCourseProgress = withErrorHandling(
  async (courseId: string) => {
    const data = await makeApiRequest(
      API_ENDPOINTS.CINEPRO.GET_COURSE_PROGRESSION(courseId),
      "GET"
    );

    // Traiter la réponse en fonction du format
    if (data && typeof data === "object") {
      const responseData = data as Record<string, unknown>;
      if ("courseId" in responseData) {
        // C'est déjà un objet de progression valide
        return {
          id: String(responseData.id || ""),
          userId: String(responseData.userId || ""),
          courseId: String(responseData.courseId || ""),
          progression: Number(responseData.progression || 0),
          completed: Boolean(responseData.completed),
          lastWatchedAt: String(
            responseData.lastWatchedAt || new Date().toISOString()
          ),
        };
      } else if (
        "progression" in responseData &&
        typeof responseData.progression === "object" &&
        responseData.progression !== null &&
        responseData.progression &&
        typeof responseData.progression === "object"
      ) {
        const progressData = responseData.progression as Record<
          string,
          unknown
        >;
        if ("courseId" in progressData) {
          // Si l'API renvoie { progression: {...} }
          return {
            id: String(progressData.id || ""),
            userId: String(progressData.userId || ""),
            courseId: String(progressData.courseId || ""),
            progression: Number(progressData.progression || 0),
            completed: Boolean(progressData.completed),
            lastWatchedAt: String(
              progressData.lastWatchedAt || new Date().toISOString()
            ),
          };
        }
      }
    }

    return null;
  },
  null
);

/**
 * Met à jour la progression d'un cours
 * @param {string} courseId ID du cours
 * @param {number} progress Pourcentage de progression
 * @param {boolean} completed Statut de complétion
 * @returns {Promise<void>}
 */
export const updateCourseProgress = withErrorHandling(
  async (courseId: string, progress: number, completed = false) => {
    const requestBody = {
      progression: progress,
      completed: completed,
    };

    await makeApiRequest(
      API_ENDPOINTS.CINEPRO.UPDATE_COURSE_PROGRESSION(courseId),
      "PUT",
      requestBody
    );
  },
  null
);

/**
 * Marque un module comme complété
 * @param {string} moduleId ID du module
 * @returns {Promise<void>}
 */
export const markModuleAsCompleted = withErrorHandling(
  async (moduleId: string) => {
    await makeApiRequest(
      API_ENDPOINTS.CINEPRO.MARK_MODULE_COMPLETED(moduleId),
      "POST",
      { completed: true }
    );
  },
  null
);

// ==========================================
// Hook personnalisé pour gérer la progression
// ==========================================

/**
 * Hook pour gérer la progression des cours avec état et actualisation
 * @returns {Object} État et fonctions de gestion de la progression
 */
export const useProgress = () => {
  const [courseProgress, setCourseProgress] = useState<CourseProgress[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fonction pour rafraîchir la progression
  const refreshProgress = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchUserProgress();
      setCourseProgress(data);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Fonction pour mettre à jour la progression d'un cours
  const updateProgress = useCallback(
    async (courseId: string, progress: number, completed: boolean) => {
      try {
        await updateCourseProgress(courseId, progress, completed);
        // Après la mise à jour, rafraîchir les données
        await refreshProgress();
        return true;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
        return false;
      }
    },
    [refreshProgress]
  );

  // Fonction pour marquer un module comme complété
  const completeModule = useCallback(
    async (moduleId: string) => {
      try {
        await markModuleAsCompleted(moduleId);
        // Après la mise à jour, rafraîchir les données
        await refreshProgress();
        return true;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
        return false;
      }
    },
    [refreshProgress]
  );

  // Charger la progression initiale
  useEffect(() => {
    refreshProgress();
  }, [refreshProgress]);

  // Renvoyer l'état et les fonctions
  return {
    courseProgress,
    loading,
    error,
    refreshProgress,
    updateProgress,
    completeModule,
  };
};

// ==========================================
// Fonctions spécifiques pour les problèmes de chargement
// ==========================================

/**
 * Récupère les modules et cours avec gestion avancée des timeouts et retries
 * @returns {Promise<{modules: Module[], courses: Course[]}>} Modules et cours accessibles
 */
export const fetchAvailableModulesAndCourses = withErrorHandling(
  async () => {
    // Augmenter le timeout pour cette requête spécifique
    const timeoutMs = 15000; // 15 secondes au lieu de 8 par défaut

    try {
      // Essayer d'abord de récupérer tous les modules
      const modulesData = await makeApiRequest(
        API_ENDPOINTS.CINEPRO.GET_MODULES,
        "GET",
        null,
        timeoutMs
      );

      const modules = adaptModulesResponse(modulesData);

      // Récupérer les données de progression pour déterminer l'accessibilité
      const progressData = await makeApiRequest(
        API_ENDPOINTS.CINEPRO.GET_PROGRESSION,
        "GET",
        null,
        timeoutMs
      );

      // Extraire les IDs des modules accessibles depuis la réponse
      let accessibleModuleIds: string[] = [];

      if (progressData && typeof progressData === "object") {
        const apiData = progressData as Record<string, unknown>;

        if (Array.isArray(apiData.accessibleModules)) {
          accessibleModuleIds = apiData.accessibleModules as string[];
        } else if (
          apiData.accessibleModules &&
          typeof apiData.accessibleModules === "string"
        ) {
          // Si c'est une chaîne, la convertir en tableau
          accessibleModuleIds = [apiData.accessibleModules];
        }
      }

      // Marquer les modules comme accessibles ou non
      const enhancedModules = modules.map((module) => ({
        ...module,
        isEnabled: accessibleModuleIds.includes(module.id) || module.isEnabled,
      }));

      // Récupérer les cours pour tous les modules accessibles
      let allCourses: Course[] = [];

      // Utiliser Promise.all pour récupérer tous les cours en parallèle
      const coursesPromises = enhancedModules
        .filter((module) => module.isEnabled)
        .map(async (module) => {
          try {
            const moduleCourses = await fetchModuleCourses(module.id);
            return moduleCourses.map((course) => ({
              ...course,
              locked: false, // Par défaut, les cours d'un module accessible sont déverrouillés
              moduleId: module.id,
            }));
          } catch (err) {
            console.warn(
              `Erreur lors de la récupération des cours du module ${module.id}:`,
              err
            );
            return [];
          }
        });

      const coursesResults = await Promise.all(coursesPromises);
      allCourses = coursesResults.flat();

      return {
        modules: enhancedModules,
        courses: allCourses,
      };
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des modules et cours:",
        error
      );

      // En cas d'erreur, essayer une approche alternative avec les données mock
      const mockModules = MOCK_MODULES;
      let mockCourses: Course[] = [];

      mockModules.forEach((module) => {
        if (module.courses) {
          mockCourses = [...mockCourses, ...module.courses];
        }
      });

      // Retourner les données mock
      return {
        modules: mockModules,
        courses: mockCourses,
      };
    }
  },
  // Fallback avec les données mock
  () => {
    const mockModules = MOCK_MODULES;
    let mockCourses: Course[] = [];

    mockModules.forEach((module) => {
      if (module.courses) {
        mockCourses = [...mockCourses, ...module.courses];
      }
    });

    return {
      modules: mockModules,
      courses: mockCourses,
    };
  }
);

// Fonction utilitaire pour obtenir les modules accessibles
export const getAccessibleModules = async (): Promise<Module[]> => {
  try {
    const { modules } = await fetchAvailableModulesAndCourses();
    return modules.filter((module) => module.isEnabled);
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des modules accessibles:",
      error
    );
    return [];
  }
};

// Fonction utilitaire pour obtenir les cours accessibles
export const getAccessibleCourses = async (): Promise<Course[]> => {
  try {
    const { courses } = await fetchAvailableModulesAndCourses();
    return courses;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des cours accessibles:",
      error
    );
    return [];
  }
};

/**
 * Récupère les cours d'un module spécifique avec leurs informations de progression
 * @param {string} moduleId ID du module
 * @returns {Promise<Course[]>} Tableau de cours avec informations de progression
 */
export const fetchModuleProgressionCourses = withErrorHandling(
  async (moduleId: string) => {
    console.log(`Récupération des cours pour le module ${moduleId}`);

    const data = await makeApiRequest(
      API_ENDPOINTS.CINEPRO.GET_MODULE_PROGRESSION_COURSES(moduleId),
      "GET"
    );

    console.log(`Réponse API pour les cours du module ${moduleId}:`, data);

    // Si la réponse contient directement les cours
    if (data && typeof data === "object" && "courses" in data) {
      const apiData = data as { courses: Course[] };
      console.log(
        `Module ${moduleId}: ${apiData.courses.length} cours trouvés via 'courses'`
      );

      return sortByOrder(
        apiData.courses.map((course) => ({
          ...course,
          locked: Boolean(course.isGreyed),
          completed: course.progression === 100,
        }))
      );
    }

    // Si la réponse est directement un tableau de cours
    if (Array.isArray(data)) {
      console.log(
        `Module ${moduleId}: ${data.length} cours trouvés via tableau`
      );

      return sortByOrder(
        data.map(
          (course: Course & { isGreyed?: boolean; progression?: number }) => ({
            ...course,
            locked: Boolean(course.isGreyed),
            completed: course.progression === 100,
          })
        )
      );
    }

    // Aucun cours trouvé, vérifier si le module existe
    console.warn(
      `Aucun cours trouvé pour le module ${moduleId}. Tentative de récupération des informations du module.`
    );

    try {
      // Tentative de récupération des informations du module
      const moduleInfo = await makeApiRequest(
        API_ENDPOINTS.CINEPRO.GET_MODULE_BY_ID_WITH_OTHER_GREYED_OUT(moduleId),
        "GET"
      );

      console.log(`Informations du module ${moduleId}:`, moduleInfo);

      // Si le module existe mais n'a pas de cours, retourner un tableau vide
      if (moduleInfo && typeof moduleInfo === "object") {
        console.log(`Le module ${moduleId} existe mais n'a pas de cours`);
        return [];
      }
    } catch (err) {
      console.error(
        `Erreur lors de la récupération des informations du module ${moduleId}:`,
        err
      );
    }

    console.warn(
      `Aucun cours trouvé pour le module ${moduleId}. Retour d'un tableau vide.`
    );
    return [];
  },
  (moduleId: string) => {
    console.log(`Utilisation des données de test pour le module ${moduleId}`);
    const mockModule = MOCK_MODULES.find((m) => m.id === moduleId);
    return mockModule?.courses || [];
  }
);

// ==========================================
// Hook pour gérer les modules et cours
// ==========================================

/**
 * Hook pour charger et gérer les modules et cours
 * @returns État et fonctions pour gérer les modules et cours
 */
export const useModulesAndCourses = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fonction pour charger les données
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Utiliser la nouvelle fonction optimisée pour récupérer les données
      const { modules: loadedModules, courses: loadedCourses } =
        await fetchAvailableModulesAndCourses();

      setModules(loadedModules);
      setCourses(loadedCourses);
    } catch (err) {
      console.error("Erreur lors du chargement des modules et cours:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Erreur inconnue";
      setError(`Impossible de charger les données: ${errorMessage}`);

      // En cas d'erreur, utiliser les données de test
      setModules(MOCK_MODULES);

      // Extraire tous les cours des modules
      const allCourses = MOCK_MODULES.flatMap((m) => m.courses || []);
      setCourses(allCourses);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les données au montage du composant
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fonction pour obtenir les cours d'un module spécifique
  const getModuleCourses = useCallback(
    (moduleId: string) => {
      return courses.filter((course) => course.moduleId === moduleId);
    },
    [courses]
  );

  // Fonction pour obtenir les modules accessibles
  const getEnabledModules = useCallback(() => {
    return modules.filter((module) => module.isEnabled);
  }, [modules]);

  // Recharger les données
  const refresh = useCallback(() => {
    loadData();
  }, [loadData]);

  return {
    modules,
    courses,
    loading,
    error,
    getModuleCourses,
    getEnabledModules,
    refresh,
  };
};

/**
 * Hook pour charger les cours d'un module spécifique avec leur progression
 * @param {string|null} moduleId ID du module à charger
 * @returns État et fonctions pour gérer les cours du module
 */
export const useModuleCourses = (moduleId: string | null) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);

  // Fonction pour charger les cours du module
  const loadModuleCourses = useCallback(
    async (moduleToLoad: string) => {
      if (!moduleToLoad) return;

      setLoading(true);
      setError(null);

      try {
        // Vérifier d'abord si le module est considéré comme complété
        try {
          // Vérifier si le module a une progression spécifique
          const moduleProgressData = await makeApiRequest(
            API_ENDPOINTS.CINEPRO.GET_MODULE_PROGRESSIONS(moduleToLoad),
            "GET"
          );

          console.log(
            `Données de progression du module ${moduleToLoad}:`,
            moduleProgressData
          );

          // Vérifier aussi si le module apparaît dans la liste générale des progressions
          const allProgressions = await makeApiRequest(
            API_ENDPOINTS.CINEPRO.GET_PROGRESSION,
            "GET"
          );

          console.log(`Toutes les progressions:`, allProgressions);

          // Vérifier les deux sources pour déterminer si le module est complété
          let moduleCompleted = false;

          // Source 1: Données de progression spécifiques au module
          if (
            moduleProgressData &&
            typeof moduleProgressData === "object" &&
            "completed" in moduleProgressData &&
            moduleProgressData.completed === true
          ) {
            console.log(
              `Module ${moduleToLoad} est marqué comme complété via l'API spécifique`
            );
            moduleCompleted = true;
          }

          // Source 2: Toutes les progressions
          if (allProgressions && Array.isArray(allProgressions)) {
            const moduleProgress = allProgressions.find(
              (p: any) => p.moduleId === moduleToLoad && p.completed === true
            );

            if (moduleProgress) {
              console.log(
                `Module ${moduleToLoad} est marqué comme complété dans la liste générale`
              );
              moduleCompleted = true;
            }
          } else if (
            allProgressions &&
            typeof allProgressions === "object" &&
            "completedModules" in allProgressions &&
            Array.isArray(allProgressions.completedModules)
          ) {
            // Format alternatif où l'API retourne un objet avec une liste de modules complétés
            if (allProgressions.completedModules.includes(moduleToLoad)) {
              console.log(
                `Module ${moduleToLoad} est dans la liste des modules complétés`
              );
              moduleCompleted = true;
            }
          }

          setIsCompleted(moduleCompleted);
        } catch (progressErr) {
          console.error(
            `Erreur lors de la vérification de l'état de complétion du module ${moduleToLoad}:`,
            progressErr
          );
        }

        // Récupérer les cours du module
        const coursesData = await fetchModuleProgressionCourses(moduleToLoad);

        // Si aucun cours n'est retourné mais que le module est complété,
        // créer des cours factices pour représenter le contenu
        if (coursesData.length === 0 && isCompleted) {
          console.log(
            `Module ${moduleToLoad} est complété mais n'a pas de cours. Affichage du message d'erreur.`
          );
          // Ne pas créer de cours factices, laisser le tableau vide pour que l'UI affiche l'erreur
          setCourses([]);
        } else {
          setCourses(coursesData);
        }
      } catch (err) {
        console.error(
          `Erreur lors du chargement des cours du module ${moduleToLoad}:`,
          err
        );
        const errorMessage =
          err instanceof Error ? err.message : "Erreur inconnue";
        setError(`Impossible de charger les capsules: ${errorMessage}`);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    },
    [isCompleted]
  );

  // Charger les cours quand le moduleId change
  useEffect(() => {
    if (moduleId) {
      loadModuleCourses(moduleId);
    } else {
      setCourses([]);
      setIsCompleted(false);
    }
  }, [moduleId, loadModuleCourses]);

  // Fonction pour mettre à jour la progression d'un cours
  const updateCourseProgressInState = useCallback(
    (courseId: string, progression: number, completed: boolean) => {
      setCourses((prevCourses) =>
        prevCourses.map((course) =>
          course.id === courseId
            ? { ...course, progression, completed }
            : course
        )
      );
    },
    []
  );

  return {
    courses,
    loading,
    error,
    isCompleted,
    loadModuleCourses,
    updateCourseProgressInState,
  };
};

/**
 * Ajoute dynamiquement un mappage d'ID de cours vers ID YouTube
 * Cette fonction est utile lorsqu'un nouveau cours est détecté sans URL vidéo valide
 * @param courseId ID du cours à mapper
 * @param youtubeId ID YouTube correspondant
 */
export const addCourseYoutubeMapping = (
  courseId: string,
  youtubeId: string
): void => {
  if (!courseId || !youtubeId) {
    console.error("addCourseYoutubeMapping: Les IDs ne peuvent pas être vides");
    return;
  }

  // Vérifier si l'ID YouTube est valide (11 caractères)
  if (youtubeId.length !== 11 || !/^[a-zA-Z0-9_-]{11}$/.test(youtubeId)) {
    console.error("addCourseYoutubeMapping: ID YouTube invalide:", youtubeId);
    return;
  }

  // Ajouter au mappage
  COURSE_TO_YOUTUBE_ID_MAP[courseId] = youtubeId;
  console.log(`Mappage ajouté: Cours ${courseId} -> YouTube ${youtubeId}`);

  // Option: Sauvegarder dans le localStorage pour persister entre les sessions
  try {
    const existingMappings = JSON.parse(
      localStorage.getItem("courseYoutubeMappings") || "{}"
    );
    existingMappings[courseId] = youtubeId;
    localStorage.setItem(
      "courseYoutubeMappings",
      JSON.stringify(existingMappings)
    );
  } catch (error) {
    console.error(
      "Erreur lors de la sauvegarde du mappage dans localStorage:",
      error
    );
  }
};

/**
 * Charge les mappages sauvegardés depuis le localStorage
 * Appelé au démarrage de l'application
 */
export const loadSavedYoutubeMappings = (): void => {
  try {
    const savedMappings = localStorage.getItem("courseYoutubeMappings");
    if (savedMappings) {
      const mappings = JSON.parse(savedMappings);
      Object.keys(mappings).forEach((courseId) => {
        COURSE_TO_YOUTUBE_ID_MAP[courseId] = mappings[courseId];
      });
      console.log(
        "Mappages cours-YouTube chargés:",
        Object.keys(mappings).length
      );
    }
  } catch (error) {
    console.error(
      "Erreur lors du chargement des mappages depuis localStorage:",
      error
    );
  }
};

/**
 * Vérifie si toutes les progressions liées à un module sont complètes
 * @param {string} moduleId ID du module à vérifier
 * @param {CourseProgress[]} progressions Toutes les progressions disponibles
 * @returns {boolean} True si toutes les progressions sont complètes
 */
export const areAllModuleProgressionsComplete = (
  moduleId: string,
  progressions: CourseProgress[]
): boolean => {
  // Filtrer les progressions qui semblent être liées à ce module
  const moduleProgressions = progressions.filter((p) =>
    String(p.courseId).includes(moduleId)
  );

  // Aucune progression pour ce module
  if (moduleProgressions.length === 0) {
    return false;
  }

  // Vérifier si toutes les progressions sont complètes
  return moduleProgressions.every((p) => p.completed);
};
